import { Button, PasswordField, TextField } from '@ads/components-react';
import { UserIcon } from '@heroicons/react/24/outline';
import { useForm } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useLogin } from '../../../hooks/useLogin';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type ApiProblemDetails = {
  title?: string;
  detail?: string;
  status?: number;
};

// ─── Schemas ──────────────────────────────────────────────────────────────────

const usernameSchema = z.string().min(1);
const passwordSchema = z.string().min(1);

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism LoginForm — formulário de autenticação.
 *
 * Exibe campos de usuário e senha com validação via TanStack Form + Zod.
 * Ao submeter com sucesso, redireciona para `/`.
 */
export function LoginForm() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { mutate: login, isPending, isError, error } = useLogin();

  const apiError = error?.response?.data as ApiProblemDetails | undefined;
  const errorMessage = apiError?.title ?? apiError?.detail ?? t('login.errors.invalidCredentials');

  const form = useForm({
    defaultValues: { user: '', password: '' },
    onSubmit: ({ value }) => {
      login(
        { data: { user: value.user, password: value.password } },
        { onSuccess: () => void navigate({ to: '/' }) },
      );
    },
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
      className="flex flex-col gap-mini w-full"
    >
      {/* E-mail */}
      <form.Field name="user" validators={{ onBlur: usernameSchema }}>
        {field => (
          <TextField
            label={t('login.fields.email')}
            id="login-email"
            fullWidth
            placeholder={t('login.fields.emailPlaceholder')}
            leadingIcon={UserIcon}
            value={field.state.value}
            onChange={e => field.handleChange(e.target.value)}
            hasError={field.state.meta.errors.length > 0}
            {...(field.state.meta.errors.length > 0
              ? { helper: t('login.errors.usernameRequired') }
              : {})}
            disabled={isPending}
            size="lg"
          />
        )}
      </form.Field>

      {/* Senha */}
      <form.Field name="password" validators={{ onBlur: passwordSchema }}>
        {field => (
          <PasswordField
            fullWidth
            label={t('login.fields.password')}
            id="login-password"
            placeholder={t('login.fields.passwordPlaceholder')}
            leadingIcon
            value={field.state.value}
            onChange={e => field.handleChange(e.target.value)}
            hasError={field.state.meta.errors.length > 0}
            {...(field.state.meta.errors.length > 0
              ? { helper: t('login.errors.passwordRequired') }
              : {})}
            disabled={isPending}
            size="lg"
            ariaShowPassword={t('login.fields.showPassword')}
            ariaHidePassword={t('login.fields.hidePassword')}
          />
        )}
      </form.Field>

      {/* Erro global de credenciais */}
      {isError && (
        <div
          className="bg-base-danger-50 rounded-sm border-hairline border-base-danger-300 p-nano"
          role="alert"
        >
          <p className="text-base-danger-700 ts-caption-xxs">{errorMessage}</p>
        </div>
      )}

      <form.Subscribe>
        <Button
          type="submit"
          hierarchy="primary"
          isLoading={isPending}
          disabled={isPending}
          className="mt-micro w-full"
          size="lg"
          fullWidth
        >
          {t('login.submit')}
        </Button>
      </form.Subscribe>
    </form>
  );
}
